import { ProductsTypes } from "../api/types/products.types";

/**
 * Groups product variants by product_id and selects the best variant for each product.
 * Selection priority: variants with stock > 0, then cheapest price, then first created.
 * If the input `variants` array is empty, an empty array is returned.
 */
export const groupProductVariants = (
  variants: ProductsTypes[],
): ProductsTypes[] => {
  // Handle empty input
  if (!variants || variants.length === 0) {
    return [];
  }

  // Group variants by product_id
  const productsMap = new Map<number, ProductsTypes[]>();
  variants.forEach((variant) => {
    const productId = variant.product_id;
    if (!productsMap.has(productId)) {
      productsMap.set(productId, []);
    }
    productsMap.get(productId)!.push(variant);
  });

  // Select best variant for each product
  const groupedProducts: ProductsTypes[] = [];
  productsMap.forEach((variants) => {
    // Sort: first with stock > 0, then by price (ascending), then by id
    const sortedVariants = [...variants].sort((a, b) => {
      // Prefer variants with stock
      if (a.stock_quantity > 0 && b.stock_quantity === 0) return -1;
      if (a.stock_quantity === 0 && b.stock_quantity > 0) return 1;
      // Then by price (cheapest first) - use discount_price if available, otherwise price
      // Use nullish coalescing (??) instead of || to handle discount_price = 0 correctly
      const priceA = a.discount_price ?? a.price;
      const priceB = b.discount_price ?? b.price;
      if (priceA !== priceB) {
        return priceA - priceB;
      }
      // Then by id (first created)
      return a.id - b.id;
    });
    groupedProducts.push(sortedVariants[0]);
  });

  return groupedProducts;
};
